import { Injectable } from '@angular/core';
import { TaskTemplate, TaskMetadataDetails, TaskStarter } from '../models/task';

/**
 * Utility service for working with task metadata
 */
@Injectable({
  providedIn: 'root'
})
export class TaskMetadataService {

  /**
   * Parse TaskMetadata string into TaskMetadataDetails object
   * @param metadataString JSON string containing task metadata
   * @returns Parsed TaskMetadataDetails or null if parsing fails
   */
  parseTaskMetadata(metadataString: string): TaskMetadataDetails | null {
    try {
      // Clean up the string (remove \r\n and escape characters)
      const cleanedString = metadataString.replace(/\\r\\n/g, '').replace(/\\\"/g, '"');
      return JSON.parse(cleanedString) as TaskMetadataDetails;
    } catch (error) {
      console.error('Failed to parse task metadata:', error);
      return null;
    }
  }

  /**
   * Convert TaskTemplate (with string metadata) to TaskStarter (with parsed metadata)
   * @param taskTemplate Template with string metadata
   * @returns TaskStarter with parsed metadata or null if parsing fails
   */
  convertTemplateToStarter(taskTemplate: TaskTemplate): TaskStarter | null {
    if (!taskTemplate.TaskMetadata) {
      console.warn('TaskTemplate has no metadata to parse');
      return null;
    }

    const parsedMetadata = this.parseTaskMetadata(taskTemplate.TaskMetadata);
    if (!parsedMetadata) {
      return null;
    }

    return {
      TaskName: taskTemplate.TaskName,
      TaskMetadata: parsedMetadata
    };
  }

  /**
   * Convert array of TaskTemplates to array of TaskStarters
   * @param taskTemplates Array of templates with string metadata
   * @returns Array of starters with parsed metadata (filters out failed parses)
   */
  convertTemplatesToStarters(taskTemplates: TaskTemplate[]): TaskStarter[] {
    return taskTemplates
      .map(template => this.convertTemplateToStarter(template))
      .filter((starter): starter is TaskStarter => starter !== null);
  }

  /**
   * Validate that a TaskMetadataDetails object has all required properties
   * @param metadata The metadata object to validate
   * @returns True if valid, false otherwise
   */
  validateTaskMetadata(metadata: TaskMetadataDetails): boolean {
    const requiredFields = ['name', 'description', 'example', 'input_type', 'input', 'output_type', 'output', 'parameters'];
    
    for (const field of requiredFields) {
      if (!(field in metadata)) {
        console.warn(`Missing required field in TaskMetadata: ${field}`);
        return false;
      }
    }

    // Validate example structure
    if (!metadata.example || 
        !Array.isArray(metadata.example['Input Data']) ||
        !Array.isArray(metadata.example['Output Data']) ||
        !Array.isArray(metadata.example.Comments)) {
      console.warn('Invalid example structure in TaskMetadata');
      return false;
    }

    return true;
  }
}
